import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import api from '../../lib/api';
import { toast } from 'sonner';
import {
  Activity,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Brain,
  Users,
  ListTodo,
  Database,
  RefreshCw,
  ChevronRight,
  Zap,
  ArrowLeft,
  LogOut,
  Gavel,
  Shield
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

// Stat Card Component - defined outside main component
const StatCard = ({ title, value, subtext, icon: Icon, color = 'blue', onClick }) => (
  <Card 
    className={`cursor-pointer hover:shadow-md transition-shadow ${onClick ? 'hover:border-blue-400' : ''}`}
    onClick={onClick}
    data-testid={`stat-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <CardContent className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-3xl font-bold mt-1">{value}</p>
          {subtext && <p className="text-xs text-gray-400 mt-1">{subtext}</p>}
        </div>
        <div className={`p-3 rounded-full bg-${color}-100`}>
          <Icon className={`w-6 h-6 text-${color}-600`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const AutonomyDashboard = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [recentEvents, setRecentEvents] = useState([]);
  const [pendingTasks, setPendingTasks] = useState([]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const [dashboardRes, eventsRes, tasksRes] = await Promise.all([
        api.get('/admin/autonomy/dashboard'),
        api.get('/admin/autonomy/events?limit=5'),
        api.get('/admin/autonomy/tasks?requires_review=true&limit=5')
      ]);
      
      setStats(dashboardRes.data);
      setRecentEvents(eventsRes.data.events || []);
      setPendingTasks(tasksRes.data.tasks || []);
    } catch (error) {
      console.error('Failed to fetch autonomy dashboard:', error);
      toast.error('Failed to load autonomy dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const statusColorMap = {
    'pending': 'bg-yellow-100 text-yellow-800',
    'in_progress': 'bg-blue-100 text-blue-800',
    'completed': 'bg-green-100 text-green-800',
    'failed': 'bg-red-100 text-red-800',
    'awaiting_input': 'bg-purple-100 text-purple-800'
  };

  const priorityColorMap = {
    'critical': 'bg-red-600 text-white',
    'high': 'bg-orange-500 text-white',
    'medium': 'bg-yellow-500 text-white',
    'low': 'bg-gray-400 text-white'
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="autonomy-dashboard">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center gap-4">
              <Link to="/admin/console" className="text-gray-600 hover:text-gray-900">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Autonomy Dashboard</h1>
                <p className="text-sm text-gray-500">AI Systems Overview</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={fetchDashboard} variant="outline" size="sm" data-testid="refresh-btn">
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button onClick={handleLogout} variant="ghost" size="sm">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <StatCard
                title="Events (24h)"
                value={stats?.events?.last_24h || 0}
                subtext={`${stats?.events?.last_7d || 0} in last 7 days`}
                icon={Activity}
                color="blue"
                onClick={() => navigate('/admin/autonomy/events')}
              />
              <StatCard
                title="Active Tasks"
                value={(stats?.tasks?.pending || 0) + (stats?.tasks?.in_progress || 0)}
                subtext={`${stats?.tasks?.completed || 0} completed`}
                icon={ListTodo}
                color="green"
                onClick={() => navigate('/admin/autonomy/tasks')}
              />
              <StatCard
                title="Active Agents"
                value={stats?.agents?.active || 0}
                icon={Brain}
                color="purple"
                onClick={() => navigate('/admin/autonomy/agents')}
              />
              <StatCard
                title="Pending Reviews"
                value={stats?.review_queue?.pending || 0}
                subtext="Requires human review"
                icon={AlertTriangle}
                color="amber"
                onClick={() => navigate('/admin/autonomy/tasks?requires_review=true')}
              />
            </div>

            {/* Secondary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-500">Task Status Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {Object.entries(stats?.tasks?.by_status || {}).map(([status, count]) => (
                      <div key={status} className="flex items-center justify-between">
                        <span className="text-sm capitalize">{status.replace('_', ' ')}</span>
                        <Badge className={statusColorMap[status] || 'bg-gray-100'}>{count}</Badge>
                      </div>
                    ))}
                    {Object.keys(stats?.tasks?.by_status || {}).length === 0 && (
                      <p className="text-sm text-gray-400">No tasks yet</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-500">AI Models</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-3xl font-bold">{stats?.models?.total || 0}</p>
                      <p className="text-xs text-gray-400">Registered models</p>
                    </div>
                    <Database className="w-8 h-8 text-gray-400" />
                  </div>
                  <Button 
                    variant="link" 
                    className="p-0 mt-2 text-sm"
                    onClick={() => navigate('/admin/autonomy/models')}
                  >
                    View Models <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-500">System Health</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-8 h-8 text-green-500" />
                    <div>
                      <p className="font-semibold text-green-700">Operational</p>
                      <p className="text-xs text-gray-400">All systems running</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recent Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Recent Events */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-lg font-semibold">Recent Events</CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/admin/autonomy/events')}>
                    View All <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </CardHeader>
                <CardContent>
                  {recentEvents.length === 0 ? (
                    <p className="text-gray-500 text-sm text-center py-4">No recent events</p>
                  ) : (
                    <div className="space-y-3">
                      {recentEvents.map((event) => (
                        <div key={event.id} className="flex items-start gap-3 p-2 rounded-lg hover:bg-gray-50">
                          <Zap className="w-4 h-4 text-blue-500 mt-1" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{event.event_type}</p>
                            <p className="text-xs text-gray-500">{event.entity_type}</p>
                          </div>
                          <span className="text-xs text-gray-400">
                            {event.created_at ? new Date(event.created_at).toLocaleTimeString() : '-'}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Pending Tasks */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-lg font-semibold">Tasks Requiring Review</CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/admin/autonomy/tasks?requires_review=true')}>
                    View All <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </CardHeader>
                <CardContent>
                  {pendingTasks.length === 0 ? (
                    <p className="text-gray-500 text-sm text-center py-4">No tasks pending review</p>
                  ) : (
                    <div className="space-y-3">
                      {pendingTasks.map((task) => (
                        <div key={task.id} className="flex items-start gap-3 p-2 rounded-lg hover:bg-gray-50">
                          <Clock className="w-4 h-4 text-amber-500 mt-1" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{task.task_name || task.task_type}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge className={statusColorMap[task.status] || 'bg-gray-100'} variant="secondary">
                                {task.status}
                              </Badge>
                              {task.priority && (
                                <Badge className={priorityColorMap[task.priority] || 'bg-gray-400'}>
                                  {task.priority}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Quick Actions */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="text-lg font-semibold">Quick Navigation</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-3">
                  <Button variant="outline" onClick={() => navigate('/admin/governance')} data-testid="nav-governance">
                    <Gavel className="w-4 h-4 mr-2" />
                    Governance Decisions
                  </Button>
                  <Button variant="outline" onClick={() => navigate('/admin/autonomy/events')} data-testid="nav-events">
                    <Activity className="w-4 h-4 mr-2" />
                    Events Log
                  </Button>
                  <Button variant="outline" onClick={() => navigate('/admin/autonomy/tasks')} data-testid="nav-tasks">
                    <ListTodo className="w-4 h-4 mr-2" />
                    Tasks Queue
                  </Button>
                  <Button variant="outline" onClick={() => navigate('/admin/autonomy/agents')} data-testid="nav-agents">
                    <Brain className="w-4 h-4 mr-2" />
                    AI Agents
                  </Button>
                  <Button variant="outline" onClick={() => navigate('/admin/autonomy/models')} data-testid="nav-models">
                    <Database className="w-4 h-4 mr-2" />
                    Model Registry
                  </Button>
                  <Button variant="outline" onClick={() => navigate('/admin/autonomy/case-memory')} data-testid="nav-case-memory">
                    <Users className="w-4 h-4 mr-2" />
                    Case Memory
                  </Button>
                  <Button variant="outline" onClick={() => navigate('/admin/autonomy/explainability')} data-testid="nav-explainability">
                    <AlertTriangle className="w-4 h-4 mr-2" />
                    Explainability Audit
                  </Button>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </main>
    </div>
  );
};

export default AutonomyDashboard;
